import { getCheckInOutReport, getCheckInOutStats } from '@/app/services/reports/reports';
import * as Actions from '@/app/store/actions';
import { Avatar } from '@material-ui/core';
import moment from 'moment-timezone';
import Pusher from 'pusher-js';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import secureLocalStorage from 'react-secure-storage';
import { useReactToPrint } from 'react-to-print';
import AppliedFiltersBar from '@shared/Reports/AppliedFiltersBar';
import ReportsHeader from '@shared/Reports/ReportsHeader';
import StatBox from '@shared/Reports/StatBox';
import TableTooltip from '@shared/Reports/TableTooltip';
import AttendanceFiltersBar from './AttendanceFiltersBar';
import AttendancePrintViewTable from './AttendancePrintTable';
import AttendanceTable from './AttendanceTable';
import { useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';

const AttendanceReports = () => {
	const [filters, setFilters] = useState({
		start_date: null,
		end_date: null,
		room_id: [],
		student_id: [],
		status: { label: 'Active', value: true },
		timezone: null,
		sort: 'date',
		dir: 'desc',
		page: 1,
		student_name: 'first',
	});
	const user = useSelector((state) => state.auth.user);
	const viewAsId = secureLocalStorage.getItem('view_as_id');
	const authToken = secureLocalStorage.getItem('jwt_access_token');
	const [loading, setLoading] = useState(false);
	const [printData, setPrintData] = useState([]);
	const [rowsPerPage, setRowsPerPage] = useState(10);
	const [printDataLoading, setPrintDataLoading] = useState(false);
	const navigate = useNavigate();
	const printRef = useRef();
	const [statsLoading, setStatsLoading] = useState(false);
	const [rows, setRows] = useState([]);
	const [totalResults, setTotalResults] = useState(0);
	const dispatch = useDispatch();
	const cache = useRef({}); // Cache object to prevent refetching pages already fetched
	const prevFilters = useRef(filters); // Track previous filters to know when to refetch a set of results
	const [statsData, setStatsData] = useState({});
	const [exportType, setExportType] = useState(null);
	const [exportLoading, setExportLoading] = useState(false);
	const exportTimeoutRef = useRef(null);

	const timezone = user?.school?.timezone || user?.data?.school?.timezone;

	useEffect(() => {
		const pusher = new Pusher(import.meta.env.VITE_PUSHER_CHANNEL_ID, {
			cluster: import.meta.env.VITE_PUSHER_CLUSTER_ID,
			channelAuthorization: {
				headers: {
					Authorization: `Bearer ${authToken}`,
				},
				endpoint: `${import.meta.env.VITE_API_ENDPOINT}broadcasting/auth`,
			},
		});
		const channel = pusher.subscribe(
			`private-App.Models.User.${user.role[0] === 'super_school_admin' ? viewAsId : user.data?.id}`
		);

		channel.bind('report.export.complete', (res) => {
			const exportLink = JSON.parse(window.localStorage.getItem('export_uuid'));
			if (exportLink && exportLink === res.uuid) {
				setExportLoading(false);
				if (exportTimeoutRef.current) {
					clearTimeout(exportTimeoutRef.current);
				}
				window.localStorage.removeItem('export_uuid');

				const anchor = document.createElement('a');
				anchor.href = res.link;
				anchor.download = res.filename;
				anchor.click();
				anchor.remove();
			}
		});

		return () => {
			pusher.disconnect();
		};
	}, []);

	const handlePrint = useReactToPrint({
		content: () => printRef.current,
	});

	useEffect(() => {
		setFilters({
			...filters,
			timezone: timezone,
		});
	}, [user]);

	const calculatePercentChange = (current, previous) => {
		if (previous === 0) {
			return current * 100;
		}
		return (((current - previous) / previous) * 100).toFixed(1);
	};

	const generateQueryParams = (filters, exportType = null) => {
		return {
			page: filters.page,
			start_date: filters.start_date,
			end_date: filters.end_date,
			status: filters.status?.value === true ? 1 : filters.status?.value === false ? 0 : null,
			timezone: filters.timezone,
			export: exportType,
			sort: filters.sort,
			dir: filters.dir,
			student_id: filters.student_id?.length > 0 ? filters.student_id.map((item) => item.value) : null,
			room_id: filters.room_id?.length > 0 ? filters.room_id.map((item) => item.value) : null,
			student_name: filters.student_name,
		};
	};

	// Helper function to parse time strings (00:00) into minutes
	const parseTime = (timeStr) => {
		const [hours, minutes] = timeStr.split(':').map(Number);
		return hours * 60 + minutes;
	};

	const formatTime = (timeString) => {
		const timeArr = timeString.split(':');
		const hours = parseInt(timeArr[0], 10);
		const minutes = parseInt(timeArr[1], 10);
		return `${hours}h ${minutes}m`;
	};

	// Helper function to update filters and reset page to 1 if any filter other than page changes
	const updateFilters = (newFilters) => {
		const filtersChanged = Object.keys(newFilters).some((key) => key !== 'page' && newFilters[key] !== filters[key]);
		if (filtersChanged) {
			setFilters({ ...newFilters, page: 1 });
		} else {
			setFilters(newFilters);
		}
	};

	useEffect(() => {
		// Clear cache if any filter other than page changes
		const filtersChanged = Object.keys(filters).some(
			(key) => key !== 'page' && filters[key] !== prevFilters.current[key]
		);
		if (filtersChanged) {
			cache.current = {};
		}
		prevFilters.current = filters;

		if (filters.start_date && filters.end_date) {
			setLoading(true);
			const queryParams = generateQueryParams(filters);
			const queryKey = JSON.stringify(queryParams);

			// Check if the results for the current page are already in the cache
			if (cache.current[queryKey]) {
				const cachedData = cache.current[queryKey];
				setRows(cachedData.data);
				setTotalResults(cachedData.total);
				setLoading(false);

				// Fetch stats data only when filters other than page change and total results > 0
				if (filtersChanged && cachedData.total > 0) {
					fetchStatsData(queryParams);
				}
			} else {
				getCheckInOutReport(queryParams)
					.then((res) => {
						setRows(res.data.data);
						setTotalResults(res.data.meta.total);
						// Store the results in the cache
						cache.current[queryKey] = {
							data: res.data.data,
							total: res.data.meta.total,
						};

						// Fetch stats data only when filters other than page change and total results > 0
						if (filtersChanged && res.data.meta.total > 0) {
							fetchStatsData(queryParams);
						} else if (filtersChanged) {
							setStatsData({});
						}
					})
					.catch(() => {
						dispatch(
							Actions.showMessage({
								message: 'Failed to fetch report',
								variant: 'error',
							})
						);
						setRows([]);
						setTotalResults(0);
						setStatsData({});
					})
					.finally(() => {
						setLoading(false);
					});
			}
		} else {
			// Clear data if start_date and end_date is not set
			setRows([]);
			setTotalResults(0);
			setStatsData({});
		}
	}, [filters]);

	const fetchStatsData = async (queryParams) => {
		setStatsLoading(true);
		try {
			const res = await getCheckInOutStats(queryParams);
			const stats = res.data;
			const previousAverageTime = parseTime(stats.previous.average_time);
			const currentAverageTime = parseTime(stats.average_time);
			const averageTimePercentChange =
				previousAverageTime === 0
					? currentAverageTime * 100
					: (((currentAverageTime - previousAverageTime) / previousAverageTime) * 100).toFixed(1);

			const formatDateRange = (start, end) => {
				const startDate = moment(start);
				const endDate = moment(end);
				if (startDate.month() === endDate.month()) {
					return `${startDate.format('MMM D')} - ${endDate.format('D')}`;
				} else {
					return `${startDate.format('MMM D')} - ${endDate.format('MMM D')}`;
				}
			};

			setStatsData({
				total_students: {
					total: stats.total_students,
					percentChange: calculatePercentChange(stats.total_students, stats.previous.total_students),
					dateRange: formatDateRange(stats.previous.start_date, stats.previous.end_date),
				},
				daily_average: {
					total: stats.daily_average,
					percentChange: calculatePercentChange(stats.daily_average, stats.previous.daily_average),
					dateRange: formatDateRange(stats.previous.start_date, stats.previous.end_date),
				},
				average_time: {
					total: formatTime(stats.average_time),
					percentChange:
						stats.previous.average_time === '00:00'
							? stats.average_time !== '00:00'
								? 100
								: 0
							: averageTimePercentChange,
					dateRange: formatDateRange(stats.previous.start_date, stats.previous.end_date),
				},
			});
		} catch (error) {
			dispatch(
				Actions.showMessage({
					message: 'Failed to fetch statistics data',
					variant: 'error',
				})
			);
			setStatsData({});
		} finally {
			setStatsLoading(false);
		}
	};

	const handleExport = async (type) => {
		if (filters.start_date && filters.end_date) {
			const queryParams = generateQueryParams(filters, type);
			try {
				setExportLoading(true);
				queryParams.uuid = uuidv4();
				window.localStorage.setItem('export_uuid', JSON.stringify(queryParams.uuid));
				await getCheckInOutReport(queryParams);

				// Set a timeout to reset exportLoading if no response within 20 seconds
				exportTimeoutRef.current = setTimeout(() => {
					if (!exportLoading) return;
					setExportLoading(false);
					dispatch(
						Actions.showMessage({
							message: 'Export failed to generate. Please try again.',
							variant: 'error',
						})
					);
				}, 20000);
			} catch (error) {
				dispatch(
					Actions.showMessage({
						message: 'Failed to export report',
						variant: 'error',
					})
				);
			}
		}
	};

	const handlePdfExport = async () => {
		// Calculate difference between start_date and end_date
		const diffDays = moment(filters.end_date).diff(moment(filters.start_date), 'days');
		if (diffDays > 7) {
			dispatch(
				Actions.showMessage({
					message:
						'PDF export over 7 days is not supported. Select to Print and Print to PDF for date ranges over 7 days',
					variant: 'warning',
				})
			);
			return;
		}
		return handleExport('pdf');
	};

	const handlePrintClick = async () => {
		setExportType('print');
		if (filters.start_date && filters.end_date) {
			setPrintDataLoading(true);
			try {
				const queryParams = generateQueryParams(filters, 'print');

				const res = await getCheckInOutReport(queryParams);
				setPrintData(res.data.data);
			} catch (error) {
				console.error('Error fetching all pages for print:', error);
			} finally {
				setPrintDataLoading(false);
			}
		}
	};

	useEffect(() => {
		if (!printDataLoading && printData.length > 0) {
			handlePrint();
		}
	}, [printDataLoading, printData]);

	const columns = [
		{
			id: 'date',
			label: 'Date',
			render: (row) => (
				<button
					className="hover:underline cursor-pointer"
					onClick={() =>
						setFilters({
							...filters,
							page: 1,
							start_date: moment(row.checkin.datetime).format('YYYY-MM-DD'),
							end_date: moment(row.checkin.datetime).format('YYYY-MM-DD'),
						})
					}
					type="button"
				>
					{moment(row.checkin.datetime).format('MM/DD/YY')}
				</button>
			),
			printRender: (row) => moment(row.checkin.datetime).format('MM/DD/YY'),
			widthClass: '',
		},
		{
			id: 'student',
			label: 'Student',
			render: (row) => {
				return (
					<div className="flex items-center gap-16">
						<Avatar src={row.student.photo} />
						<TableTooltip>
							<button
								className="hover:underline cursor-pointer"
								onClick={() => {
									setFilters({
										...filters,
										page: 1,
										student_id: [
											{
												label: `${row.student.first_name} ${row.student.last_name}`,
												value: row.student_id,
											},
										],
									});
								}}
								type="button"
							>
								{filters.student_name === 'first'
									? `${row.student.first_name} ${row.student.last_name}`
									: `${row.student.last_name}, ${row.student.first_name}`}
							</button>
						</TableTooltip>
					</div>
				);
			},
			printRender: (row) => {
				return filters.student_name === 'first'
					? `${row.student.first_name} ${row.student.last_name}`
					: `${row.student.last_name}, ${row.student.first_name}`;
			},
			widthClass: 'min-w-256',
		},
		{
			id: 'room',
			label: 'Room',
			render: (row) => (
				<div className="flex items-center gap-16">
					<Avatar src={row.room.photo} />
					<button
						className="hover:underline cursor-pointer"
						onClick={() =>
							setFilters({
								...filters,
								page: 1,
								room_id: [{ label: row.room.name, value: row.room.id }],
							})
						}
						type="button"
					>
						{row.room.name}
					</button>
				</div>
			),
			printRender: (row) => row.room.name,
			widthClass: 'min-w-200',
		},
		{
			id: 'checkin_by',
			widthClass: 'min-w-200',
			label: 'Check-In by',
			render: (row) => row.checkin.name,
			printRender: (row) => row.checkin.name,
		},
		{
			id: 'checkin',
			label: 'In Time',
			render: (row) => moment(row.checkin.datetime).tz(timezone).format('hh:mm A z'),
			printRender: (row) => moment(row.checkin.datetime).tz(timezone).format('hh:mm A z'),
			widthClass: 'max-w-96',
		},
		{
			id: 'checkout',
			label: 'Out Time',
			widthClass: 'max-w-96',
			render: (row) =>
				row.checkout?.datetime ? `${moment(row.checkout.datetime).tz(timezone).format('hh:mm A z')}` : <>&mdash;</>,
			printRender: (row) =>
				row.checkout?.datetime ? moment(row.checkout.datetime).tz(timezone).format('hh:mm A z') : <>&mdash;</>,
		},
		{
			id: 'checkout_by',
			label: 'Check-Out by',
			widthClass: 'min-w-200',
			render: (row) => row.checkout.name,
			printRender: (row) => row.checkout.name,
		},
		{
			id: 'hours',
			label: 'Total Hrs',
			render: (row) => (row.total_hours !== '00:00' ? row.total_hours : <>&mdash;</>),
			widthClass: '',
			printRender: (row) => (row.total_hours !== '00:00' ? row.total_hours : <>&mdash;</>),
		},
	];
	return (
		<div>
			<ReportsHeader
				exportLoading={exportLoading}
				exportsDisabled={!rows?.length}
				onBack={() => navigate(-1)}
				onDownloadPDF={handlePdfExport}
				onExport={handleExport}
				onPrint={handlePrintClick}
				pdfLoading={false}
				printLoading={printDataLoading}
				subtitle="Track check-in and check-out times, who signed students in and out, and total hours present."
				title="Attendance Report"
			/>
			<AttendanceFiltersBar filters={filters} setFilters={updateFilters} />
			<AppliedFiltersBar filters={filters} setFilters={updateFilters} />
			<div className="bg-white">
				{Object.keys(statsData).length > 0 ? (
					<div className="px-32 py-48">
						<div className="flex flex-col sm:flex-row sm:justify-between gap-16 md:gap-36 sm:max-w-4xl mx-auto">
							<StatBox
								data={statsData.total_students}
								loading={statsLoading || loading}
								title="Total Students"
								type="totalStudent"
							/>
							<StatBox
								data={statsData.daily_average}
								loading={statsLoading || loading}
								title="Avg. Daily Students"
								type="dailyStudent"
							/>
							<StatBox
								data={statsData.average_time}
								loading={statsLoading || loading}
								title="Avg. Daily Time Attended"
								type="dailyTime"
							/>
						</div>
					</div>
				) : null}
				<div>
					<AttendanceTable
						columns={columns}
						data={rows}
						dir={filters.dir}
						filters={filters}
						loading={loading}
						page={filters.page}
						rowsPerPage={rowsPerPage}
						setDir={(dir) => updateFilters({ ...filters, dir })}
						setFilters={updateFilters}
						setPage={(page) => updateFilters({ ...filters, page })}
						setRowsPerPage={setRowsPerPage}
						setSort={(sort) => updateFilters({ ...filters, sort })}
						sort={filters.sort}
						totalResults={totalResults}
					/>
				</div>
				<div className="h-0 overflow-hidden">
					<AttendancePrintViewTable
						columns={columns}
						data={printData}
						exportType={exportType}
						filters={filters}
						ref={printRef}
						title="Attendance Report"
						user={user}
					/>
				</div>
			</div>
		</div>
	);
};

export default AttendanceReports;
